import React, { useEffect, useRef, useState } from 'react';
import { IAddress } from 'services/GoogleMapsService';
import { useGoogleMapsLoader } from 'hooks/googleMaps';

import TextField from '../TextField';
import { createAutocompleteOptions, parseAddressComponents } from './utils';
import { useDropdownPosition } from './hooks';

// css insert for google places dropdown override
import './styles.css';
interface IAddressAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  onAddressSelect?: (address: IAddress) => void;
  label: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  allowedCountries?: string[];
  testId?: string;
}

const AddressAutocomplete = ({
  value,
  onChange,
  onAddressSelect,
  label,
  required,
  disabled,
  error,
  placeholder,
  allowedCountries = [],
  testId,
}: IAddressAutocompleteProps): JSX.Element => {
  const [placeAutocomplete, setPlaceAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isLoaded, mapsError } = useGoogleMapsLoader();

  // Setup dropdown positioning
  useDropdownPosition(inputRef.current);

  // Initialize autocomplete when maps is loaded
  useEffect(() => {
    if (!isLoaded || !inputRef.current || mapsError || !window.google?.maps?.places) return undefined;

    const input = inputRef.current;
    const options = createAutocompleteOptions(allowedCountries);

    // Clean up any existing PAC containers that might interfere with new instance
    const existingContainers = document.querySelectorAll('.pac-container');
    existingContainers.forEach(container => container.remove());

    try {
      const autocomplete = new google.maps.places.Autocomplete(input, options);
      autocomplete.setFields(['address_components', 'geometry', 'formatted_address']);
      setPlaceAutocomplete(autocomplete);
    } catch (error) {
      console.error('Error creating Google Maps Autocomplete:', error);
    }

    return () => {
      setPlaceAutocomplete(prev => {
        if (prev) {
          try {
            google.maps.event.clearInstanceListeners(prev);
          } catch (error) {
            console.warn('Error clearing autocomplete listeners:', error);
          }
        }
        return null;
      });
    };
  }, [isLoaded, allowedCountries, mapsError]);

  useEffect(() => {
    if (!placeAutocomplete) return undefined;

    const handlePlaceSelect = () => {
      const place = placeAutocomplete.getPlace();
      const address = parseAddressComponents(place);

      if (!address) return;

      onChange(address.addressLine1);
      onAddressSelect?.(address);
    };

    const listener = placeAutocomplete.addListener('place_changed', handlePlaceSelect);
    return () => google.maps.event.removeListener(listener);
  }, [placeAutocomplete, onChange, onAddressSelect]);

  const extraProps =
    isLoaded && !mapsError
      ? {
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          },
        }
      : {};

  return (
    <TextField
      inputRef={inputRef}
      label={label}
      required={required}
      disabled={disabled}
      error={error}
      value={value}
      placeholder={placeholder}
      testId={testId}
      onChange={e => onChange(e.target.value)}
      {...extraProps}
    />
  );
};

export default AddressAutocomplete;
